/* live_v2/flip.js — FLIP (First Last Invert Play) reflow animation.
 *
 * When the user drops / resizes a widget, sibling widgets reflow to new
 * positions. Without animation, they teleport — the user sees a sudden
 * new layout and can't tell which widget moved where. With FLIP, the
 * displacement is animated smoothly so the cause-effect chain is visible.
 *
 * Usage:
 *   const before = LSFlip.capture(gridEl);   // BEFORE the layout mutation
 *   // … mutate state, let Alpine re-render via $nextTick …
 *   LSFlip.play(gridEl, before);             // AFTER (in nextTick callback)
 *
 * Mechanism — for each [data-flip-id] descendant of `gridEl`:
 *   1. First  — capture old bounding rect
 *   2. Last   — read new bounding rect after re-render
 *   3. Invert — apply translate equal to (old - new) so visually it's at old
 *   4. Play   — transition transform to identity over `dur` ms
 *
 * Cells that didn't move skip the animation (zero delta short-circuit).
 * Cells that are newly mounted have no `before` entry and skip too — they
 * just appear in place, no animation flicker.
 */
window.LSFlip = window.LSFlip || {
  /** Snapshot every flip-tracked element's position before a layout change. */
  capture(root) {
    if (!root) return new Map();
    const out = new Map();
    root.querySelectorAll('[data-flip-id]').forEach((el) => {
      const id = el.getAttribute('data-flip-id');
      if (!id) return;
      const r = el.getBoundingClientRect();
      out.set(id, { x: r.left, y: r.top });
    });
    return out;
  },

  /** Animate every flip-tracked element from its old position to its new
   *  position. Idempotent on re-entry — earlier transitions are cancelled
   *  by setting transition='none' then restoring it next frame. */
  play(root, before, opts) {
    if (!root || !before || !before.size) return;
    // reduced motion: things land where they go, no slide (v2 run 2, FIT-16)
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const dur  = (opts && opts.dur)  || 260;
    // Cubic-bezier matches the rest of the dashboard (KPI hover, cells).
    const ease = (opts && opts.ease) || 'cubic-bezier(0.22, 1, 0.36, 1)';
    root.querySelectorAll('[data-flip-id]').forEach((el) => {
      const id = el.getAttribute('data-flip-id');
      if (!id) return;
      const prev = before.get(id);
      if (!prev) return;                       // new mount — no FLIP
      const r  = el.getBoundingClientRect();
      const dx = prev.x - r.left;
      const dy = prev.y - r.top;
      // No movement → skip. Avoids needless paints and lets the transition
      // be free for hover/edit-mode highlights.
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;

      // Cancel any in-flight transition.
      el.style.transition = 'none';
      el.style.transform  = 'translate3d(' + dx + 'px, ' + dy + 'px, 0)';

      // Next frame — flip transform back to identity with the timing fn.
      requestAnimationFrame(() => {
        el.style.transition = 'transform ' + dur + 'ms ' + ease;
        el.style.transform  = 'translate3d(0, 0, 0)';
        const cleanup = (ev) => {
          if (ev && ev.propertyName !== 'transform') return;
          el.style.transition = '';
          el.style.transform  = '';
          el.removeEventListener('transitionend', cleanup);
        };
        el.addEventListener('transitionend', cleanup);
        // Safety: if transitionend never fires (cell unmounted etc.),
        // clean up after dur+50ms so styles don't leak.
        setTimeout(cleanup, dur + 50);
      });
    });
  },
};
